import {ActionState, type CurrentState, type MechanicRobberColonistRushState, type MechanicTurnColonistRushState, type NestedReadonly, type PlayerColor} from '@colonist/game-states'

export class CurrentStateValidator {
    private readonly _currentState: NestedReadonly<CurrentState>
    private readonly _experimentalCurrentState: {currentTurnPlayers: PlayerColor[]} | undefined
    private readonly _mechanicTurnColonistRushState: NestedReadonly<MechanicTurnColonistRushState> | undefined
    private readonly _mechanicRobberColonistRushState: NestedReadonly<MechanicRobberColonistRushState> | undefined
    private readonly _playerStates: {[key in PlayerColor]?: { actionState: ActionState }} | undefined
    private readonly _playerColors: PlayerColor[]

    constructor(currentState: NestedReadonly<CurrentState>, experimentalCurrentState: {currentTurnPlayers: PlayerColor[]} | undefined,
                mechanicTurnColonistRushState: NestedReadonly<MechanicTurnColonistRushState> | undefined, playerStates: {[key in PlayerColor]?: { actionState: ActionState }} | undefined,
                playerColors: PlayerColor[], mechanicRobberColonistRushState: NestedReadonly<MechanicRobberColonistRushState> | undefined) {
        this._currentState = currentState
        this._experimentalCurrentState = experimentalCurrentState
        this._mechanicTurnColonistRushState = mechanicTurnColonistRushState
        this._mechanicRobberColonistRushState = mechanicRobberColonistRushState
        this._playerStates = playerStates
        this._playerColors = playerColors
    }

    get currentState(): NestedReadonly<CurrentState> {
        return this._currentState
    }

    // WARNING! This architecture is fragile. This only works because the base game is force setting currentTurnPlayerColor until the refactor is
    // done. This is silently coupled to CurrentStateControllerDefault.setCurrentTurnPlayer
    get currentTurnPlayers(): PlayerColor[] {
        return this._experimentalCurrentState?.currentTurnPlayers ?? [this._currentState.currentTurnPlayerColor]
    }

    get nextPlayerToMoveRobber(): PlayerColor | undefined {
        return this._mechanicRobberColonistRushState?.playerToMoveRobberOnNextSevenRoll ?? undefined
    }

    get currentActivePlayers(): PlayerColor[] {
        if(this._mechanicTurnColonistRushState) {
            const playersDiscardingCards = this.currentTurnPlayers.filter((playerColor) => this.getPlayerActionState(playerColor) == ActionState.SelectCardsToDiscard)
            if(playersDiscardingCards.length > 0) return playersDiscardingCards

            const playersToggledEndTurn = this.playersToggledEndTurn
            const playersActive = this.currentTurnPlayers.filter((playerColor) => !playersToggledEndTurn.includes(playerColor))
            return playersActive
        } else {
            return this.currentTurnPlayers
        }
    }

    get playersToggledEndTurn(): PlayerColor[] {
        const playersToggledEndTurn = this._mechanicTurnColonistRushState?.playersToggledEndTurn
        if(playersToggledEndTurn == undefined) return []

        return Object.keys(playersToggledEndTurn).map((key) => parseInt(key, 10) as PlayerColor)
    }

    // WARNING! This architecture is fragile. This only works because the base game is force setting actionState until the refactor is
    // done. This is silently coupled to CurrentStateControllerDefault.setPlayerActionState
    getPlayerActionState(playerColor: PlayerColor): ActionState {
        return this._playerStates?.[playerColor]?.actionState ?? this._currentState.actionState
    }

    hasPlayerToggledEndTurn(playerColor: PlayerColor): boolean {
        return this.playersToggledEndTurn.includes(playerColor)
    }

    isSomePlayerInActionState(actionState: ActionState): boolean {
        return this._playerColors.some((playerColor) => this.getPlayerActionState(playerColor) === actionState)
    }

    getPlayersInActionState(actionState: ActionState): PlayerColor[] {
        return this._playerColors.filter((playerColor) => this.getPlayerActionState(playerColor) === actionState)
    }

    isOpponentInKnightBlockingState(player: PlayerColor): boolean {
        const blockerActionStates = [ActionState.PlaceRobberOrPirate, ActionState.PlaceRobberOnly, ActionState.PlacePirateOnly, ActionState.SelectWhoToRob]
        return this._playerColors.some((playerColor) => {
            if(playerColor === player) return false
            return blockerActionStates.includes(this.getPlayerActionState(playerColor))
        })
    }
}
